import { useEffect, useRef, useState } from "react"

import { CheckOutlined, ClockCircleOutlined, DownOutlined } from "@ant-design/icons"
import { Dropdown, Tooltip } from "antd"
import classNames from "classnames"
import { userSelector, getUserAvatarUrl } from 'features/user/model/selectors/userSelectors'
import { useGetEnablePermission } from "features/public-link-files/hooks/useGetEnablePermission"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { getAttachmentPreviewLink } from "shared/utils/filesUtils"
import { formatTime } from "shared/utils/time"
import styled, { createGlobalStyle } from "styled-components"

import {
  // getCommentsCountByStatusSelector,
  getFilesPreviewActiveTabSelector,
  getFilesPreviewAnnotationsSelector,
  getFilesPreviewCommentIdForScrollToSelector,
  getFilesPreviewCommentsFilterSelector,
  getFilesPreviewItemsSelector,
  getFilesPreviewTabsContentLoadedSelector,
  getFilteredCommentsSelector,
  getNewAnnotationSelector,
  getPlayerCurrentTimeSelector,
  getPlayerIsPlayingSelector,
  getReplyingCommentSelector,
  getSelectedAnnotationSelector
} from '../model/selectors/filesPreviewSelectors'
import {
  createCommentThunk,
  deleteCommentThunk,
  filesPreviewActions,
  updateCommentStatusThunk,
  updateCommentThunk
} from "../model/slice/filesPreviewSlice"
import { useAppId } from '../../../shared/hooks'

import { Comment, CommentType, ICommentAttachment } from "./Comment"
import { MessageWriter } from "./MessageWriter"
import { TabContentLoader } from "./TabContentLoader"

const calculateCommentsCount = (comments: CommentType[]) => {
  let result = 0

  comments.forEach((comment) => {
    result += 1

    if (comment.replies?.length) {
      result += comment.replies?.length
    }
  })

  return result
}

export interface ICommentsTabContent {
  onAnyFocusChange?: (focused: boolean) => void
  currentIndex: number
}

export const CommentsTabContent: React.FC<ICommentsTabContent> = ({ onAnyFocusChange, currentIndex }) => {
  const dispatch = useAppDispatch()
  const items = useAppSelector(getFilesPreviewItemsSelector)
  const comments = useAppSelector(getFilteredCommentsSelector)
  // const commentsCounts = useAppSelector(getCommentsCountByStatusSelector)
  const currentFilter = useAppSelector(getFilesPreviewCommentsFilterSelector)
  const contentLoaded = useAppSelector(getFilesPreviewTabsContentLoadedSelector)
  const currentItem = items[currentIndex]
  const user = useAppSelector(userSelector)
  const selectedAnnotation = useAppSelector(getSelectedAnnotationSelector)
  const videoCurrentTime = useAppSelector(getPlayerCurrentTimeSelector)
  const isPlaying = useAppSelector(getPlayerIsPlayingSelector)
  const allAnnotations = useAppSelector(getFilesPreviewAnnotationsSelector)
  const commentIdForScrollTo = useAppSelector(getFilesPreviewCommentIdForScrollToSelector)
  const replyingCommentId = useAppSelector(getReplyingCommentSelector)
  const newAnnotation = useAppSelector(getNewAnnotationSelector)
  const activeTab = useAppSelector(getFilesPreviewActiveTabSelector)
  const [newCommentFocused, setNewCommentFocused] = useState(false)
  const [commentFocused, setCommentFocused] = useState(false)
  const { isCreateCommentsEnabled } = useGetEnablePermission()
  const currentUserAvatar = useAppSelector(getUserAvatarUrl)
  const { isAivard } = useAppId()

  const authorized = !!user

  const [needTimestamp, setNeedTimestamp] = useState(false)

  const isVideo = currentItem?.file_info?.type === 'video'

  const userName = user?.name
  const userId = user?.id

  const commentsContainerRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    if (commentsContainerRef.current) {
      commentsContainerRef.current.scrollTop = commentsContainerRef.current.scrollHeight
    }
  }

  useEffect(() => {
    if (activeTab === 'comments' && comments.length > 0) {
      scrollToBottom()
    }
  }, [activeTab, comments.length, comments?.[0]?.id])

  useEffect(() => {
    if (isPlaying) {
      setNeedTimestamp(false)
    } else {
      setNeedTimestamp(true)
    }
  }, [isPlaying])

  useEffect(() => {
    onAnyFocusChange?.(newCommentFocused || commentFocused)
  }, [newCommentFocused, commentFocused])

  const handleFilterChange = (filter: 'all' | 'open' | 'resolved') => {
    dispatch(filesPreviewActions.setCommentsFilter(filter))
  }

  const MenuItem = ({ text, isSelected }: { text: string; isSelected: boolean }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>
        {text}
      </span>

      {isSelected && <CheckOutlined style={{ color: '#CDA8F0' }} />}
    </div>
  )

  const filterMenuItems = [
    {
      key: 'all',
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            All comments
          </span>

          {currentFilter === 'all' && (
            <SCheckOutlined />
          )}
        </div>
      ),
      onClick: () => handleFilterChange('all'),
    },
    {
      key: 'open',
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            Open
          </span>

          {currentFilter === 'open' && (
            <SCheckOutlined />
          )}
        </div>
      ),
      onClick: () => handleFilterChange('open'),
    },
    {
      key: 'resolved',
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            Resolved
          </span>

          {currentFilter === 'resolved' && (
            <SCheckOutlined   />
          )}
        </div>
      ),
      onClick: () => handleFilterChange('resolved'),
    },
  ]

  // useLayoutEffect(() => {
  //   if (commentIdForScrollTo && comments.length > 0) {
  //     console.log('commentIdForScrollTo', commentIdForScrollTo)
  //     setTimeout(() => {
  //       const commentElement = document.querySelector(`div[data-comment-id="${commentIdForScrollTo}"]`)

  //       console.log('commentElement', commentElement)

  //       if (commentElement) {
  //         commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
  //       }

  //       // dispatch(filesPreviewActions.setCommentIdForScrollTo(null))
  //     }, 100)
  //   }
  // }, [commentIdForScrollTo, comments])

  const handleCommentMount = (commentId: string) => {
    if (commentId === commentIdForScrollTo) {
      const commentElement = document.querySelector(`div[data-comment-id="${commentIdForScrollTo}"]`)

      if (commentElement) {
        commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      dispatch(filesPreviewActions.setCommentIdForScrollTo(null))
    }
  }

  const handleCommentUpdate = (id: number, content: string) => {
    dispatch(updateCommentThunk({ nodeId: currentItem.id, commentId: id, message: content, mentions: [] }))
  }

  const handleCommentDelete = (id: number) => {
    dispatch(deleteCommentThunk({ nodeId: currentItem.id, commentId: id }))
  }

  const handleCommentAdd = async (data: any) => {
    const annotation = isVideo && needTimestamp ? {
      type: 'point',
      offset: videoCurrentTime,
      coordinates: [-1, -1],
    } : null

    const result = await dispatch(createCommentThunk({ nodeId: currentItem.id, message: data.message, parent_id: null, annotation, mentions: data.mentions }))

    if (result.type === 'filesPreview/createCommentThunk/fulfilled' && (result.payload as any)?.isAIMentioned) {
      dispatch(filesPreviewActions.addCommentWaitingForAiReply((result.payload as any).commentId))
    }

    setTimeout(scrollToBottom, 100)
  }

  const handleReplySend = async (id: number, data: any) => {
    const result = await dispatch(createCommentThunk({ nodeId: currentItem.id, message: data.message, parent_id: id, annotation: null, mentions: data.mentions }))

    if (result.type === 'filesPreview/createCommentThunk/fulfilled' && (result.payload as any)?.isAIMentioned) {
      dispatch(filesPreviewActions.addCommentWaitingForAiReply(String(id)))
    }
  }

  const handleClickReply = (commentId: number) => {
    dispatch(filesPreviewActions.setReplyingCommentId(String(commentId)))
  }

  /**
   * Показываем выбранную аннотацию на основе комментария
   * @param comment - Комментарий, содержащий информацию об аннотации
   * @param allAnnotations - Массив всех аннотаций для поиска соответствующей
   */
  const selectAnnotationFromComment = (comment: CommentType, allAnnotations: any[]) => {
    const selectedAnnotation = allAnnotations.find((annotation) => annotation.id === comment.id)

    if (!selectedAnnotation || !selectedAnnotation.annotation) {
      console.warn('Аннотация не найдена для комментария:', comment.id)
      return
    }

    if (!selectedAnnotation.annotation.coordinates || selectedAnnotation.annotation.coordinates.length < 2) {
      console.warn('Некорректные координаты аннотации для комментария:', comment.id)
      return
    }

    const [x1, y1, x2, y2] = selectedAnnotation.annotation.coordinates
    let width = 0
    let height = 0

    if(x2 && y2) {
      width = x2 - x1
      height = y2 - y1
    }

    if(newAnnotation) {
      dispatch(filesPreviewActions.setNewAnnotation(null))
    }

    dispatch(filesPreviewActions.setIsShowAnnotationLayer(true))
    dispatch(filesPreviewActions.setIsShowAnnotationMarkersLayer(true))
    dispatch(filesPreviewActions.setIsCreatingNewAnnotation(false))
    dispatch(filesPreviewActions.setSelectedAnnotation({
      data: {
        id: selectedAnnotation.id,
        text: selectedAnnotation.message,
        author_name: selectedAnnotation.author_name,
        author_id: selectedAnnotation.author_id,
        created: selectedAnnotation.created
      },
      type: selectedAnnotation.annotation.type,
      coordinates: selectedAnnotation.annotation.coordinates,
      offset: selectedAnnotation.annotation.offset,
      geometry: {
        absolute: {
          x: 0,
          y: 0,
          width: 0,
          height: 0,
        },
        relative: {
          x: x1,
          y: y1,
          width: width,
          height: height,
        }
      },
    }))
    dispatch(filesPreviewActions.setCreatingNewAnnotationMode(null))
  }

  const handleCommentClick = (comment: CommentType) => {

    if (isVideo && comment.annotation?.offset) {
      dispatch(filesPreviewActions.setPlayerIsPlaying(false))
      dispatch(filesPreviewActions.setPlayerCurrentTime(comment.annotation.offset))
    }

    if (comment.annotation) {
      selectAnnotationFromComment(comment, allAnnotations)
    }
  }

  const handleTimestampClick = () => {
    if (isPlaying) {
      dispatch(filesPreviewActions.setPlayerIsPlaying(false))
    }

    setNeedTimestamp(!needTimestamp)
  }

  const isActiveByTimestamp = (comment: CommentType) => {
    return videoCurrentTime > 0 && comment.annotation?.offset === videoCurrentTime
  }

  const isActiveBySelection = (comment: CommentType) => {
    return selectedAnnotation?.data?.id === comment.id
  }

  const handleAttachmentClick = (attachment: ICommentAttachment, commentId: string) => {
    const previewUrl = getAttachmentPreviewLink(attachment)

    if (previewUrl) {
      dispatch(filesPreviewActions.setOverlayImageData({
        url: previewUrl,
        type: 'ai',
        versionId: attachment.id,
        versionData: attachment,
        sourceCommentId: String(commentId),
        currentNodeData: currentItem
      }))
    }
  }

  const handleCommentStatusChange = (commentId: number, newStatus: 'open' | 'resolved') => {
    dispatch(updateCommentStatusThunk({ nodeId: currentItem.id, commentId, status: newStatus }))
  }


  if (!contentLoaded) {
    return <TabContentLoader />
  }

  return (
    <SCommentsTabContent>
      <STabHeader>
        <Dropdown
          overlayClassName="filter-dropdown"
          menu={{ items: filterMenuItems }}
          placement="bottom"
          trigger={['click']}
        >
          <SFilterContainer>

            <SFilterButton>
              {currentFilter === 'all' &&
                `All comments`
              }

              {currentFilter === 'open' &&
                `Open`
              }

              {currentFilter === 'resolved' &&
                `Resolved`
              }

              <DownOutlined />
            </SFilterButton>

          </SFilterContainer>
        </Dropdown>

        <SCounterComments>
          <SChatIcon
            src="/icons/chat-icon.svg"
            alt="Chat"
          />

          {calculateCommentsCount(comments)}
        </SCounterComments>

      </STabHeader>

      {comments.length > 0 && (
        <SCommentsContainer ref={commentsContainerRef}>
          {comments.map((comment, index) => (
            <SComment
              id={comment.id}
              key={comment.id}
              replyingCommentId={replyingCommentId}
              {...comment}
              onEdit={handleCommentUpdate}
              onDelete={handleCommentDelete}
              onReply={handleReplySend}
              onClickReply={handleClickReply}
              currentUserName={userName}
              currentUserId={userId}
              currentUserAvatar={currentUserAvatar}
              videoTimestamp={comment.annotation?.offset}
              onCommentClick={() => isCreateCommentsEnabled && handleCommentClick(comment)}
              isEditEnabled={isCreateCommentsEnabled}
              isActive={isActiveByTimestamp(comment) || isActiveBySelection(comment)}
              isRepliesVisibleByDefault={comment.id === commentIdForScrollTo}
              onMount={handleCommentMount}
              hideReplyBtn={!isCreateCommentsEnabled}
              onAnyFocusChange={setCommentFocused}
              isNewAIMessage={comment.isNewAIMessage}
              className={classNames({ first: index === 0 })}
              attachments={comment?._embedded?.attachments}
              onAttachmentClick={handleAttachmentClick}
              onStatusChange={handleCommentStatusChange}
            />
          ))}
        </SCommentsContainer>
      )}

      {comments.length === 0 && (
        <SCommentsContainer ref={commentsContainerRef}>
          <SCommentsEmpty>
            No comments yet
          </SCommentsEmpty>
        </SCommentsContainer>
      )}

      {isCreateCommentsEnabled && (
        <SBottomRow>
          <MessageWriter
            className="new-comment-writer"
            type="new"
            onSend={handleCommentAdd}
            onAnnotateClick={() => {
              // Annotation functionality not implemented yet
            }}
            menuPosition="top"
            onFocusChange={setNewCommentFocused}
            additionalBottomContent={(
              isVideo && (
                <Tooltip title="Timestamp">
                  <STimestampButton
                    $isAivard={isAivard}
                    contentEditable={false}
                    onClick={handleTimestampClick}
                    className={classNames({ active: needTimestamp })}
                  >
                    <ClockCircleOutlined />

                    {needTimestamp && formatTime(videoCurrentTime)}
                  </STimestampButton>
                </Tooltip>
              )
            )}
          />
        </SBottomRow>
      )}

      <GlobalStyleForDropdown />
    </SCommentsTabContent>
  )
}

const SCheckOutlined = styled(CheckOutlined)`
  font-size: 12px;
`

const SCounterComments = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`


const SCommentsTabContent = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 156px);
  user-select: text;
`

const STabHeader = styled.div`
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  border-bottom: 1px solid rgba(48, 48, 48, 1);
  font-size: 14px;
`

const SFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
`

const SFilterButton = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  border-radius: 999px;
  border: 1px solid transparent;
  padding: 6px 12px;
  padding-left: 0px;
  
  &:hover, &.active {
    border-radius: 6px;
  }
`

const SChatIcon = styled.img`
  width: 24px;
  height: 24px;
`

const SCommentsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 12px 16px;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 4px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-thumb {
    background-color: gray;
    border-radius: 14px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`

const SBottomRow = styled.div`
  padding: 0 24px;
`

const SCommentsEmpty = styled.div`
  text-align: center;
  font-size: 14px;
  color: #808080;
`

const STimestampButton = styled.button<{ $isAivard: boolean }>`
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
  border-radius: 999px;
  border: 1px solid transparent;
  padding: 6px;
  
  &:hover, &.active {
    border-radius: 6px;
    color: ${({ $isAivard }) => $isAivard ? '#CDA8F0' : '#7f7ae8'};
    border: ${({ $isAivard }) => $isAivard ? '1px solid #9968d6' : '1px solid #7f7ae8'};
    background: ${({ $isAivard }) => $isAivard ? '#482b6f' : '#36357e'};
  }
`

const SComment = styled(Comment)`
  &.first {
    margin-top: auto;
  }
`

const GlobalStyleForDropdown = createGlobalStyle`
  .filter-dropdown .ant-dropdown-menu {
    background: #424242 !important;
    border-radius: 6px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;

    .ant-dropdown-menu-item {
    color: #fff !important;
    min-width: 120px !important;
    
    &:hover {
      background: #555 !important;
    }
  }

  .ant-dropdown-menu-item-selected {
    background: #555 !important;
    color: #CDA8F0 !important;
  }
  }


`
// const SIconContainer = styled.div<{ $active: boolean }>`
//   display: flex;
//   padding: 4px;
//   border-radius: 999px;
//   background: ${({ $active }) => ($active ? 'transparent' : 'rgba(255, 255, 255, 0.08)')};
// `
